import { useEffect, useMemo, useRef } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { useQuery } from '@tanstack/react-query';
import { fetchCountryRegions } from '@worldcoo/monolith/forms/regions';
import { NativeSelect, NativeSelectOption } from '../native-select';
import { cn } from '../../lib/utils';

export type NativeRegionFieldTexts = {
	placeholder: string;
};

/**
 * Same data and styling as RegionField, but rendered as a real <select> so browsers can autofill
 * it — see the note in native-country-field.tsx.
 *
 * Caveat on the token: browsers have no concept of a Spanish comunidad autónoma. For ES the
 * `address-level1` token carries the *provincia* ("Barcelona"), which will not match any option
 * here, so passing that token to this field fills nothing in Spain. It is the province field that
 * lines up with address-level1; this component is shaped so it can back that field too.
 */
export default function NativeRegionField({
	label,
	country,
	className,
	texts,
	optional,
	autoComplete,
}: {
	label: string;
	country?: string | undefined;
	className?: string;
	texts: NativeRegionFieldTexts;
	optional?: boolean;
	autoComplete?: string;
}) {
	const field = useFieldContext<string>();
	const formReadOnly = useFormReadOnly();
	const isOptional = useIsFieldOptional(field.name, optional);

	// Mirrors RegionField: a new country invalidates whatever region was selected.
	const prevCountryRef = useRef(country);
	useEffect(() => {
		if (prevCountryRef.current !== country) {
			prevCountryRef.current = country;
			field.handleChange('');
		}
	}, [country, field]);

	const { data: regions = [], isSuccess } = useQuery({
		queryKey: ['countries', country],
		queryFn: !country ? () => [] : () => fetchCountryRegions(country).then((data) => data ?? []),
	});

	const hasNoRegions = isSuccess && regions.length === 0;

	const options = useMemo(() => regions.map(({ code, name }) => ({ value: code, label: name })), [regions]);

	return (
		<div className={cn('flex flex-col gap-2', className, (!country || hasNoRegions) && 'hidden')}>
			{label && (
				<FieldLabelRow
					label={label}
					htmlFor={field.name}
					hasError={field.state.meta.isTouched && !field.state.meta.isValid}
					optional={isOptional}
				/>
			)}
			<NativeSelect
				className="w-full"
				id={field.name}
				name={field.name}
				autoComplete={autoComplete}
				aria-invalid={hasFieldError(field.state.meta)}
				disabled={formReadOnly}
				value={field.state.value ?? ''}
				onBlur={field.handleBlur}
				onChange={(event) => field.handleChange(event.target.value)}
			>
				<NativeSelectOption value="">{texts.placeholder}</NativeSelectOption>
				{options.map((item) => (
					<NativeSelectOption key={item.value} value={item.value}>
						{item.label}
					</NativeSelectOption>
				))}
			</NativeSelect>
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
